<template>
  <validation-observer ref="clientForm" #default="{ handleSubmit }">
    <b-form @submit.prevent="handleSubmit(onSubmit)">
      <b-row v-if="localClient !== null">
        <!-- Type de client -->
        <b-col cols="12">
          <validation-provider
              #default="{ errors }"
              name="Type de client"
              rules="required"
          >
            <b-form-group label="Type de client" label-for="client-type">
              <b-form-radio-group
                  id="client-type"
                  v-model="localClient.type"
                  :options="typeOptions"
                  :state="checkErrors(errors)"
                  class="d-flex"
                  name="client-type"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Nom -->
        <b-col cols="12" md="6">
          <validation-provider
              #default="{ errors }"
              name="Nom"
              rules="required"
          >
            <b-form-group label="Nom" label-for="client-name">
              <b-form-input
                  id="client-name"
                  v-model="localClient.name"
                  :state="checkErrors(errors)"
                  placeholder="Nom du particulier ou de l'entreprise"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Contact (pour entreprises) -->
        <b-col v-if="localClient.type === 'company'" cols="12" md="6">
          <validation-provider
              #default="{ errors }"
              name="Personne de contact"
              rules="required"
          >
            <b-form-group label="Personne de contact" label-for="contact-person">
              <b-form-input
                  id="contact-person"
                  v-model="localClient.details.contact_person"
                  :state="checkErrors(errors)"
                  placeholder="Nom de la personne à contacter"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Email -->
        <b-col cols="12" md="6">
          <validation-provider
              #default="{ errors }"
              name="Email"
              rules="required|email"
          >
            <b-form-group label="Email" label-for="client-email">
              <b-form-input
                  id="client-email"
                  v-model="localClient.details.email"
                  :state="checkErrors(errors)"
                  placeholder="email@exemple.com"
                  type="email"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Téléphone -->
        <b-col cols="12" md="6">
          <validation-provider
              #default="{ errors }"
              name="Téléphone"
              rules="required"
          >
            <b-form-group label="Téléphone" label-for="client-phone">
              <b-form-input
                  id="client-phone"
                  v-model="localClient.details.phone"
                  :state="checkErrors(errors)"
                  placeholder="00 229 01 98 80 63 63"
                  type="tel"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Country -->
        <b-col cols="12" md="6">
          <validation-provider
              #default="{ errors }"
              name="client-country"
              rules="required"
          >
            <b-form-group
                :state="checkErrors(errors)"
                label="Pays"
                label-for="client-country"
            >
              <v-select
                  v-model="localClient.country"
                  :clearable="false"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="countriesOptions"
                  :reduce="(val) => val.code"
                  :selectable="
                      (option) => checkSelectedOption(option)
                    "
                  input-id="client-country"
                  label="name"
                  placeholder="Sélectionner le pays du client..."
              >
                <template #no-options="{ search, searching }">
                  <template v-if="searching">
                    Pas de résultat pour <em>{{ search }}</em
                  >.
                  </template>
                  <em v-else style="opacity: 0.5"
                  >Recherche du client...</em
                  >
                </template>
              </v-select>
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Address -->
        <b-col cols="12" md="6">
          <validation-provider
              #default="{ errors }"
              name="Addresse"
              rules="required"
          >
            <b-form-group label="Adresse" label-for="client-address">
              <b-form-input
                  id="client-address"
                  v-model="localClient.details.address"
                  :state="checkErrors(errors)"
                  placeholder="123 Rue de SOS village des enfants, Abomey Calavi, Atlantique, Bénin"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Notes -->
        <b-col cols="12">
          <b-form-group label="Notes" label-for="client-notes">
            <b-form-textarea
                id="client-notes"
                v-model="localClient.details.notes"
                placeholder="Notes supplémentaires sur le client..."
                rows="3"
            />
          </b-form-group>
        </b-col>

        <!-- Boutons d'action -->
        <b-col class="mt-2" cols="12">
          <b-button :disabled="loading" class="mr-1" type="submit" variant="primary">
            <b-spinner v-if="loading" small/>
            <span>Enregistrer</span>
          </b-button>
          <b-button
              variant="outline-secondary"
          >
            Annuler
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </validation-observer>
</template>

<script>
import {ValidationObserver, ValidationProvider} from 'vee-validate'
import {required} from '@core/utils/validations/validations'
import vSelect from 'vue-select'
import {
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormRadioGroup,
  BFormTextarea,
  BRow,
  BSpinner
} from "bootstrap-vue";
import store from "@/store";

export default {
  components: {
    BFormInvalidFeedback,
    BFormTextarea,
    BSpinner,
    BButton,
    BFormInput,
    BFormRadioGroup,
    BFormGroup,
    BCol,
    BRow,
    BForm,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  props: {
    client: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localClient: null,
      typeOptions: [
        {text: 'Particulier', value: 'individual'},
        {text: 'Entreprise', value: 'company'},
      ],
      statusOptions: [
        {text: 'Actif', value: 'active'},
        {text: 'Inactif', value: 'inactive'},
      ],
      countriesOptions: [],
      required,
    }
  },
  watch: {
    client: {
      handler(val) {
        this.localClient = {...val}
      },
      immediate: true
    },
  },
  mounted() {
    this.fetchClientProperties();
  },
  methods: {
    async fetchClientProperties() {
      const props = ["countries"];
      const {resource} = await store.dispatch("client/fetchProperties", {props})
      this.countriesOptions = resource.countries ?? [];
    },
    checkErrors(errors) {
      if (errors === undefined) return false;
      return errors.length > 0 ? false : null;
    },
    onSubmit() {
      this.$emit('submit', this.localClient)
    },
    checkSelectedOption(option) {
      return !option.code.includes('nothing_selected');
    }
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select';
</style>